import { z } from 'zod';
import {
  DEFAULT_POS_SETTINGS,
  FOOD_TYPE_KEYS,
  ORDER_SOURCE_KEYS,
  ORDER_TYPE_KEYS,
  PAYMENT_MODE_KEYS,
  PRICE_MODES,
  REFUND_MODES,
  ROUNDING_MODES,
} from '../constants/pos.js';
import { validateNumberFormat } from '../billing/numbering.js';
import { VPA_RE } from '../billing/upi.js';
import { crmSettingsFields, customerProfileFields } from './crm.js';
import { deviceSettingsFields } from './devices.js';
import { operationsSettingsFields } from './operations.js';
import {
  email,
  fssai,
  gstin,
  isoDate,
  name,
  objectId,
  optional,
  pagination,
  phone,
  reason,
  requiredText,
  text,
} from './common.js';

const paise = z.coerce.number().int().min(0).max(100_000_000_00);
const rateBp = z.coerce.number().int().min(0).max(10_000);
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (24-hour)');
const station = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9 _-]{2,30}$/, '2–30 letters/digits');

// ---------------------------------------------------------------- menu
export const taxGroupSchema = z.strictObject({
  name: requiredText(60),
  gstRateBp: rateBp,
  cessRateBp: rateBp.default(0),
  isDefault: z.boolean().default(false),
});

export const categorySchema = z.strictObject({
  name: requiredText(60),
  sortOrder: z.coerce.number().int().min(0).max(10_000).default(0),
  color: optional(z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Use a hex colour like #F97316')),
  kitchenStation: station.default('kitchen'),
  isActive: z.boolean().default(true),
});

const optionSchema = z.strictObject({
  id: objectId.optional(),
  name: requiredText(60),
  pricePaise: paise.default(0),
  isActive: z.boolean().default(true),
});

export const modifierGroupSchema = z
  .strictObject({
    name: requiredText(60),
    minSelect: z.coerce.number().int().min(0).max(20).default(0),
    maxSelect: z.coerce.number().int().min(1).max(20).default(1),
    options: z.array(optionSchema).min(1, 'Add at least one option').max(40),
  })
  .refine((g) => g.minSelect <= g.maxSelect, { message: 'Minimum cannot exceed maximum', path: ['minSelect'] })
  .refine((g) => g.minSelect <= g.options.length, {
    message: 'Not enough options for the minimum',
    path: ['minSelect'],
  });

const variantSchema = z.strictObject({
  id: objectId.optional(),
  name: requiredText(40),
  pricePaise: paise,
  barcode: optional(text(40)),
  isActive: z.boolean().default(true),
});

export const itemSchema = z
  .strictObject({
    categoryId: objectId,
    name: requiredText(80),
    shortName: optional(text(32)),
    description: optional(text(300)),
    shortCode: optional(
      z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9]{1,8}$/, '1–8 letters/digits'),
    ),
    barcodes: z.array(z.string().trim().min(3).max(40)).max(10).default([]),
    foodType: z.enum(FOOD_TYPE_KEYS).default('veg'),
    taxGroupId: objectId,
    hsnSac: optional(
      z
        .string()
        .trim()
        .regex(/^\d{4,8}$/, 'HSN/SAC is 4–8 digits'),
    ),
    priceMode: z.enum(PRICE_MODES).default('inclusive'),
    pricePaise: paise.optional(),
    variants: z.array(variantSchema).max(20).default([]),
    modifierGroupIds: z.array(objectId).max(10).default([]),
    isCombo: z.boolean().default(false),
    comboComponents: z
      .array(
        z.strictObject({
          itemId: objectId,
          variantId: optional(objectId),
          qty: z.coerce.number().int().min(1).max(20),
        }),
      )
      .max(10)
      .default([]),
    kitchenStation: optional(station),
    isFavorite: z.boolean().default(false),
    sortOrder: z.coerce.number().int().min(0).max(10_000).default(0),
    scheduleIds: z.array(objectId).max(10).default([]),
    isActive: z.boolean().default(true),
  })
  .refine((i) => i.variants.length > 0 || i.pricePaise != null, {
    message: 'Set a price, or add variants with their own prices',
    path: ['pricePaise'],
  });

export const scheduleSchema = z.strictObject({
  name: requiredText(40),
  days: z.array(z.coerce.number().int().min(0).max(6)).min(1, 'Pick at least one day').max(7),
  from: hhmm,
  to: hhmm,
  isActive: z.boolean().default(true),
});

export const outletItemOverrideSchema = z.strictObject({
  variantId: optional(objectId),
  pricePaise: optional(paise),
  isAvailable: z.boolean().default(true),
});

export const stockToggleSchema = z.strictObject({
  variantId: optional(objectId),
  outOfStock: z.boolean(),
});

// ---------------------------------------------------------------- coupons & customers
export const couponSchema = z
  .strictObject({
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9]{3,20}$/, '3–20 letters/digits'),
    description: optional(text(120)),
    type: z.enum(['percent', 'flat']),
    valueBp: rateBp.optional(),
    valuePaise: paise.optional(),
    minBillPaise: paise.default(0),
    maxDiscountPaise: optional(paise),
    validFrom: optional(isoDate),
    validTo: optional(isoDate),
    usageLimit: optional(z.coerce.number().int().min(1)),
    outletIds: z.array(objectId).default([]),
    isActive: z.boolean().default(true),
  })
  .refine((c) => (c.type === 'percent' ? c.valueBp > 0 : c.valuePaise > 0), {
    message: 'Enter the discount value',
    path: ['valueBp'],
  });

export const customerSchema = z.strictObject({
  phone,
  name: optional(name),
  email: optional(email),
  gstin: optional(gstin),
  marketingConsent: z.boolean().default(false),
  ...customerProfileFields,
});

export const customerLookupQuery = z.strictObject({
  phone: z
    .string()
    .trim()
    .regex(/^\+?\d{3,15}$/, 'Digits only'),
});

// ---------------------------------------------------------------- bills
const discountSpec = z.discriminatedUnion('type', [
  z.strictObject({ type: z.literal('percent'), valueBp: rateBp }),
  z.strictObject({ type: z.literal('flat'), valuePaise: paise }),
]);

export const billLineInput = z.strictObject({
  lineId: z.string().uuid(),
  itemId: objectId,
  variantId: optional(objectId),
  modifierOptionIds: z.array(objectId).max(40).default([]),
  qty: z.coerce.number().int().min(1).max(999),
  note: optional(text(140)),
  discount: discountSpec.nullable().optional(),
});

export const paymentInput = z.strictObject({
  mode: z.enum(PAYMENT_MODE_KEYS),
  amountPaise: paise,
  tenderedPaise: paise.optional(),
  reference: optional(text(60)),
});

export const billInput = z.strictObject({
  clientUuid: z.string().uuid(),
  action: z.enum(['hold', 'kot', 'complete']),
  orderType: z.enum(ORDER_TYPE_KEYS),
  source: z.enum(ORDER_SOURCE_KEYS).default('counter'),
  tableNo: optional(text(12)),
  guestCount: optional(z.coerce.number().int().min(1).max(500)),
  customerId: optional(objectId),
  lines: z.array(billLineInput).min(1, 'Add at least one item').max(200),
  billDiscount: z
    .discriminatedUnion('type', [
      z.strictObject({ type: z.literal('percent'), valueBp: rateBp, reason: optional(text(120)) }),
      z.strictObject({ type: z.literal('flat'), valuePaise: paise, reason: optional(text(120)) }),
    ])
    .nullable()
    .optional(),
  couponCode: optional(text(20)),
  payments: z.array(paymentInput).max(10).optional(),
  notes: optional(text(300)),
  expectedTotalPaise: paise.optional(),
});

export const cancelBillSchema = z.strictObject({ reason });
export const voidBillSchema = z.strictObject({ reason });

export const refundSchema = z
  .strictObject({
    reason,
    full: z.boolean().default(false),
    lines: z
      .array(z.strictObject({ lineId: z.string().uuid(), qty: z.coerce.number().int().min(1) }))
      .max(200)
      .default([]),
    refundMode: z.enum(REFUND_MODES).default('cash'),
  })
  .refine((r) => r.full || r.lines.length > 0, { message: 'Choose items to refund', path: ['lines'] });

export const billListQuery = z.strictObject({
  status: optional(z.string().max(60)),
  from: optional(isoDate),
  to: optional(isoDate),
  q: optional(text(40)),
  outletId: objectId.optional(),
  ...pagination,
});

export const shareReceiptSchema = z.strictObject({ to: email });

// ---------------------------------------------------------------- settings
const numberFormat = (opts) =>
  z
    .string()
    .trim()
    .superRefine((v, ctx) => {
      const r = validateNumberFormat(v, opts);
      if (!r.ok) ctx.addIssue({ code: 'custom', message: r.error });
    });

const lineList = z.array(z.string().trim().max(48)).max(5);

export const posSettingsSchema = z.strictObject({
  rounding: z.enum(ROUNDING_MODES),
  defaultPriceMode: z.enum(PRICE_MODES),
  invoiceFormat: numberFormat(),
  creditNoteFormat: numberFormat(),
  orderTypes: z.strictObject(Object.fromEntries(ORDER_TYPE_KEYS.map((k) => [k, z.boolean()]))),
  tableRequiredForDineIn: z.boolean(),
  sources: z.strictObject(Object.fromEntries(ORDER_SOURCE_KEYS.map((k) => [k, z.boolean()]))),
  discountAuditThresholdPct: z.coerce.number().min(0).max(100),
  receipt: z.strictObject({
    headerLines: lineList,
    footerLines: lineList,
    showGstin: z.boolean(),
    showFssai: z.boolean(),
    showCashier: z.boolean(),
    showCustomer: z.boolean(),
    showTaxBreakup: z.boolean(),
    showUpiQr: z.boolean(),
    paperWidth: z.enum(['58mm', '80mm']),
    showLogo: z.boolean().default(DEFAULT_POS_SETTINGS.receipt.showLogo),
    showLinkQr: z.boolean().default(DEFAULT_POS_SETTINGS.receipt.showLinkQr),
    fontSize: z.enum(['normal', 'large']).default(DEFAULT_POS_SETTINGS.receipt.fontSize),
  }),
  kot: z.strictObject({ large: z.boolean(), showCreatedBy: z.boolean() }).default({ ...DEFAULT_POS_SETTINGS.kot }),
  tokenSlip: z
    .strictObject({ enabled: z.boolean(), footer: optional(text(80)) })
    .default({ ...DEFAULT_POS_SETTINGS.tokenSlip }),
  printing: z
    .strictObject({ autoPrintReceipt: z.boolean(), autoPrintKot: z.boolean(), openDrawerOnCash: z.boolean() })
    .default({ ...DEFAULT_POS_SETTINGS.printing }),
  ...operationsSettingsFields,
  ...crmSettingsFields,
  ...deviceSettingsFields,
});

const chargeOrderTypes = z.array(z.enum(ORDER_TYPE_KEYS)).max(3);

export const outletPosSchema = z.strictObject({
  upi: z.strictObject({
    vpa: optional(z.string().trim().regex(VPA_RE, 'Enter a UPI ID like shop@okhdfcbank')),
    payeeName: optional(text(50)),
  }),
  serviceCharge: z.strictObject({
    enabled: z.boolean(),
    rateBp: rateBp.max(2000),
    taxGroupId: optional(objectId),
    orderTypes: chargeOrderTypes,
  }),
  packagingCharge: z.strictObject({
    enabled: z.boolean(),
    mode: z.enum(['per_bill', 'per_item']),
    amountPaise: paise,
    taxGroupId: optional(objectId),
    orderTypes: chargeOrderTypes,
  }),
});

export const businessProfileSchema = z.strictObject({
  businessName: requiredText(120),
  legalName: optional(text(160)),
  gstin: optional(gstin),
  fssaiNo: optional(fssai),
  phone: optional(phone),
  email: optional(email),
});
